import {
    Input,
    Table,
    Button,
    Select,
    Row,
    Col,
    Empty,
    InputNumber,
    DatePicker,
    Spin,
    notification,
    message,
} from "antd";
import {
    PlusOutlined,
    DeleteOutlined,
    CheckCircleFilled,
    LoadingOutlined,
} from "@ant-design/icons";
import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getBranches, BranchItem } from "../../../api/Branch";
import { getChannelCustomerDetail } from "../../../api/ChannelCustomer";
import { mixPostV1, mixGetV1 } from "../../../services/mixin";
import { extractApiError, isFieldRequiredError } from "../../../lib/apiError";
import useAuthStore from "../../../store/authStore";
import { useDebounce } from "../../../hooks/useDebounce";
import dayjs from "dayjs";

const { TextArea } = Input;

interface Props {
    customerId?: number;
    onDirtyChange?: (dirty: boolean) => void;
    onSubmitSuccess?: () => void;
}

export interface CreateOrderRef {
    submit: () => Promise<boolean>;
}

const CreateOrder = forwardRef<CreateOrderRef, Props>(({ customerId, onDirtyChange, onSubmitSuccess }, ref) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { isSuperAdmin, hasPermission } = useAuthStore();

    const [branches, setBranches] = useState<{ label: string; value: number }[]>([]);
    const [services, setServices] = useState<any[]>([]);
    const [loadingServices, setLoadingServices] = useState(false);
    const [products, setProducts] = useState<any[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchService, setSearchService] = useState("");
    const debouncedSearch = useDebounce(searchService, 400);
    const searchInputRef = useRef<any>(null);

    const [branchId, setBranchId] = useState<number | null>(null);
    const [staffId, setStaffId] = useState<number[]>([]);
    // Thông tin khách hàng (kênh + hồ sơ CRM) để bổ sung đầy đủ params khi tạo lịch hẹn
    const [customerInfo, setCustomerInfo] = useState<any>(null);
    const [note, setNote] = useState("");
    const [scheduledAt, setScheduledAt] = useState<dayjs.Dayjs | null>(null);
    const [discount, setDiscount] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    // Dirty detection
    const isDirty = branchId !== null || products.length > 0 || note !== "" || scheduledAt !== null || discount !== 0;

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    // Fetch branches
    const fetchBranches = useCallback(async () => {
        try {
            const res = await getBranches();
            if (res?.status && res.data?.items) {
                setBranches(res.data.items.map((b: BranchItem) => ({
                    label: b.name,
                    value: b.id,
                })));
            } else if (res?.status && Array.isArray(res.data)) {
                setBranches(res.data.map((b: BranchItem) => ({
                    label: b.name,
                    value: b.id,
                })));
            }
        } catch (e) {
            console.error("Failed to fetch branches:", e);
        }
    }, []);

    // Fetch services from API
    const fetchServices = useCallback(async (search?: string) => {
        setLoadingServices(true);
        try {
            const params: any = { take: 15, status: 0 };
            if (search) params.search = search;
            const res = await mixGetV1('/api/v1/be/services', params);
            if (res?.status && res.data?.items) {
                setServices(res.data.items);
            }
        } catch (e) {
            console.error("Failed to fetch services:", e);
        } finally {
            setLoadingServices(false);
        }
    }, []);

    useEffect(() => {
        fetchBranches();
    }, [fetchBranches]);

    // Lấy thông tin khách hàng (kênh + hồ sơ CRM) để bổ sung params khi tạo lịch hẹn
    useEffect(() => {
        if (!customerId) {
            setCustomerInfo(null);
            return;
        }
        let active = true;
        getChannelCustomerDetail(customerId).then((res) => {
            if (!active || !res.status || !res.data) return;
            setCustomerInfo(res.data);
        });
        return () => { active = false; };
    }, [customerId]);

    // Fetch services when dropdown opens or search changes
    useEffect(() => {
        if (showDropdown) {
            fetchServices(debouncedSearch || undefined);
        }
    }, [showDropdown, debouncedSearch, fetchServices]);

    /* ---------------------- */
    /* Add product/service    */
    /* ---------------------- */

    const addProduct = (product: any) => {
        const exist = products.find(p => p.id === product.id);
        if (exist) {
            setProducts(products.map(p =>
                p.id === product.id ? { ...p, qty: p.qty + 1 } : p
            ));
        } else {
            setProducts([...products, { ...product, qty: 1 }]);
        }
        setShowDropdown(false);
        setSearchService("");
    };

    const handleToggleDropdown = () => {
        const next = !showDropdown;
        setShowDropdown(next);
        if (next) {
            setSearchService("");
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    };

    const updateQty = (id: number, qty: number) => {
        setProducts(products.map(p => p.id === id ? { ...p, qty } : p));
    };

    const removeProduct = (id: number) => {
        const updated = products.filter(p => p.id !== id);
        setProducts(updated);
    };

    // Hiển thị lỗi tạo lịch hẹn: map message API theo ngôn ngữ đang chọn.
    // Riêng lỗi thiếu địa chỉ: hướng dẫn cập nhật địa chỉ + mở panel Thông tin khách hàng, focus ô Địa chỉ.
    const handleSubmitError = (source: any) => {
        if (isFieldRequiredError(source, "address")) {
            message.warning(t("roomchat.appointment_need_address") || "Khách hàng chưa có địa chỉ. Vui lòng cập nhật địa chỉ để tạo lịch hẹn.");
            window.dispatchEvent(new CustomEvent("focus-customer-address"));
            return;
        }
        message.error(extractApiError(source, i18n.language) || t("roomchat.appointment_create_failed"));
    };

    /* ---------------------- */
    /* Calculate totals       */
    /* ---------------------- */

    const productTotal = products.reduce((sum, p) => sum + p.price * p.qty, 0);
    const totalOrder = productTotal - discount;

    /* ---------------------- */
    /* Submit appointment     */
    /* ---------------------- */

    const handleSubmit = async (): Promise<boolean> => {
        if (!customerId) {
            message.error(t("roomchat.no_customer"));
            return false;
        }
        if (!branchId) {
            message.error(t("roomchat.select_branch_required"));
            return false;
        }

        setSubmitting(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
            const currentUserId = Number(currentUser?.id || 0);
            const canAssignOthers = isSuperAdmin || hasPermission("appointment.assign");

            // Thông tin khách: lấy MỚI NHẤT tại thời điểm tạo lịch hẹn để tránh dùng
            // dữ liệu cũ trong state (vd vừa cập nhật địa chỉ xong ở panel Thông tin khách hàng)
            let ch: any = customerInfo || {};
            try {
                const fresh = await getChannelCustomerDetail(customerId, { fresh: true });
                if (fresh.status && fresh.data) {
                    ch = fresh.data;
                    setCustomerInfo(fresh.data);
                }
            } catch { /* lỗi mạng -> dùng dữ liệu đã có trong state */ }
            const crm: any = ch.customer || {};
            const pick = (...vals: any[]) => vals.find((v) => v !== undefined && v !== null && v !== "");

            const payload: any = {
                channel_customer_id: customerId,
                branch_id: branchId,
                status: 1,
            };

            // --- Form ---
            if (products.length > 0) payload.service_ids = products.map(p => p.id);
            if (scheduledAt) payload.scheduled_at = scheduledAt.format("YYYY-MM-DD HH:mm:ss");
            if (note) payload.note = note;

            // --- Liên kết khách hàng CRM (nếu đã tạo) ---
            const crmId = pick(crm.id, ch.customer_id);
            if (crmId) payload.customer_id = crmId;

            // --- Thông tin khách hàng (đầy đủ params, chỉ gửi khi có dữ liệu) ---
            const customerName = pick(crm.display_name, ch.display_name);
            if (customerName) payload.customer_name = customerName;
            const customerPhone = pick(crm.phone, ch.phone);
            if (customerPhone) payload.customer_phone = customerPhone;
            const customerEmail = pick(crm.email, ch.email);
            if (customerEmail) payload.customer_email = customerEmail;
            const customerGender = pick(crm.gender, ch.gender);
            if (customerGender) payload.customer_gender = customerGender;
            const customerDob = pick(crm.birthday, ch.birthday);
            if (customerDob) payload.customer_dob = customerDob;
            const provinceCode = pick(crm.province_code, ch.province_code);
            if (provinceCode) payload.province_code = provinceCode;
            const wardCode = pick(crm.ward_code, ch.ward_code);
            if (wardCode) payload.ward_code = wardCode;
            const address = pick(crm.address, ch.address);
            if (address) payload.address = address;

            // Bắt buộc đủ Tỉnh/Thành phố, Xã/Phường và Địa chỉ mới cho tạo lịch hẹn.
            // Thiếu -> báo rõ field thiếu + mở panel Thông tin khách hàng, highlight các ô tương ứng.
            // Riêng khách nước ngoài (is_foreign = 1) không có địa giới trong nước -> chỉ bắt Địa chỉ.
            const isForeign = Number(pick(crm.is_foreign, ch.is_foreign)) === 1;
            const missingFields: string[] = [];
            if (!isForeign && !provinceCode) missingFields.push("province_code");
            if (!isForeign && !wardCode) missingFields.push("ward_code");
            if (!address) missingFields.push("address");
            if (missingFields.length > 0) {
                const fieldLabels: Record<string, string> = {
                    province_code: t("roomchat.province"),
                    ward_code: t("roomchat.ward"),
                    address: t("roomchat.address"),
                };
                const fields = missingFields.map((f) => fieldLabels[f]).join(", ");
                message.warning(
                    t("roomchat.appointment_need_location", { fields })
                    || `Vui lòng cập nhật ${fields} của khách hàng để tạo lịch hẹn`
                );
                window.dispatchEvent(new CustomEvent("focus-customer-address", { detail: { fields: missingFields } }));
                return false;
            }

            // --- Phân công: staff_id (trực page) & telesale_id lấy từ hồ sơ khách ---
            const staffIdFromCustomer = pick(crm.staff_id, ch.staff_id);
            if (staffIdFromCustomer) payload.staff_id = staffIdFromCustomer;
            const telesaleId = pick(crm.telesale_id, ch.telesale_id);
            if (telesaleId) payload.telesale_id = telesaleId;

            // Nhân viên không có quyền phân công: nếu khách chưa có trực page thì gán chính mình
            if (!canAssignOthers && !payload.staff_id && Number.isFinite(currentUserId) && currentUserId > 0) {
                payload.staff_id = currentUserId;
            }

            const res = await mixPostV1('/api/v1/be/appointments', payload);
            if (res?.status) {
                const createdId = Number(
                    res?.data?.id ??
                    res?.data?.appointment_id ??
                    res?.data?.appointment?.id ??
                    0
                );
                notification.success({
                    message: t("roomchat.order_success"),
                    description: t("roomchat.appointment_created_desc"),
                    placement: "topRight",
                    icon: <CheckCircleFilled style={{ color: "#52c41a" }} />,
                });
                // Reset form
                setBranchId(null);
                setStaffId([]);
                setNote("");
                setScheduledAt(null);
                setProducts([]);
                setDiscount(0);
                onSubmitSuccess?.();
                if (createdId > 0) {
                    navigate(`/appointment/detail/${createdId}`);
                }
                return true;
            } else {
                handleSubmitError(res);
                return false;
            }
        } catch (e: any) {
            handleSubmitError(e);
            return false;
        } finally {
            setSubmitting(false);
        }
    };

    useImperativeHandle(ref, () => ({
        submit: handleSubmit,
    }));

    /* ---------------------- */
    /* Table columns          */
    /* ---------------------- */

    const columns = [
        { title: t("roomchat.product"), dataIndex: "name" },
        {
            title: t("roomchat.quantity"),
            width: 120,
            render: (_: any, record: any) => (
                <InputNumber
                    min={1}
                    value={record.qty}
                    onChange={(val: any) => updateQty(record.id, val)}
                />
            ),
        },
        {
            title: t("roomchat.subtotal"),
            width: 140,
            render: (_: any, record: any) =>
                (record.qty * record.price).toLocaleString(),
        },
        {
            width: 50,
            render: (_: any, record: any) => (
                <DeleteOutlined
                    className="cursor-pointer"
                    onClick={() => removeProduct(record.id)}
                />
            ),
        },
    ];

    return (
        <div className="space-y-3">

            {/* Branch select */}
            <Select
                placeholder={t("roomchat.select_branch")}
                className="w-full"
                value={branchId}
                onChange={(val) => setBranchId(val)}
                options={branches}
                showSearch
                filterOption={(input, option) =>
                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                }
                allowClear
            />

            {/* Scheduled time */}
            <DatePicker
                showTime
                className="w-full dark:bg-[#1f1f1f] dark:border-[#303030] dark:text-white"
                placeholder={t("roomchat.scheduled_at")}
                value={scheduledAt}
                onChange={(val) => setScheduledAt(val)}
                format="DD/MM/YYYY HH:mm"
            />

            {/* Product/Service table */}
            <Table
                columns={columns}
                dataSource={products}
                rowKey="id"
                pagination={false}
                size="small"
                locale={{
                    emptyText: <Empty description={t("roomchat.no_data")} />,
                }}
                className="custom-ant-table"
                scroll={{ x: "max-content" }}
            />

            {/* Add service */}
            <div className="flex justify-end relative">
                <Button
                    icon={<PlusOutlined />}
                    onClick={handleToggleDropdown}
                >
                    {t("roomchat.add_service")}
                </Button>

                {showDropdown && (
                    <div className="absolute bottom-[40px] right-0 w-[320px] bg-white dark:bg-[#1f1f1f] border rounded-lg shadow-lg p-2 z-50">
                        <Input
                            ref={searchInputRef}
                            placeholder={t("roomchat.search_product")}
                            value={searchService}
                            onChange={(e) => setSearchService(e.target.value)}
                            allowClear
                            className="mb-2 dark:bg-[#1f1f1f] dark:border-[#303030] dark:text-white dark:placeholder:text-gray-400"
                        />
                        <div className="max-h-[280px] overflow-y-auto">
                        {loadingServices ? (
                            <div className="flex justify-center py-6">
                                <Spin indicator={<LoadingOutlined spin />} />
                            </div>
                        ) : services.length > 0 ? (
                            services.map((svc) => (
                                <div
                                    key={svc.id}
                                    onClick={() => addProduct({ id: svc.id, name: svc.name, price: Number(svc.price) || 0 })}
                                    className="flex gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-[#2a2a2a]"
                                >
                                    <span className="flex-1 line-clamp-2 text-sm">{svc.name}</span>
                                    <span className="whitespace-nowrap text-sm font-medium">{Number(svc.price || 0).toLocaleString()}</span>
                                </div>
                            ))
                        ) : (
                            <div className="text-center text-gray-400 py-4">{t("roomchat.no_data")}</div>
                        )}
                        </div>
                    </div>
                )}
            </div>

            {/* Note */}
            <TextArea
                rows={3}
                placeholder={t("roomchat.internal_note")}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="dark:bg-[#1f1f1f] dark:border-[#303030] dark:text-white"
            />

            {/* Price summary */}
            <div className="space-y-2">
                <Row gutter={10}>
                    <Col span={14}>{t("roomchat.discount")}</Col>
                    <Col span={10}>
                        <InputNumber
                            value={discount}
                            onChange={(v: any) => setDiscount(v || 0)}
                            className="w-full"
                        />
                    </Col>
                </Row>

                <Row gutter={10}>
                    <Col span={14}>{t("roomchat.total_order")}</Col>
                    <Col span={10}>
                        <Input value={totalOrder.toLocaleString()} disabled />
                    </Col>
                </Row>
            </div>

        </div>
    );
});

export default CreateOrder;